import type { LibraryExternalLink } from '@/shared/helpers/api.ts';

export type ExternalLinkCode = {
    name: string;
    identity: string;
    description?: string | null;
    attributes: {
        definitionName: string;
        value: string | boolean | number | null;
        referenceCode?: { identity: string } | null;
    }[];
};

export type ResolvedExternalLink = {
    text: string;
    url?: string;
    missing: string[];
};

const PLACEHOLDER_REGEX = /\$([A-Za-z_][A-Za-z0-9_]*)\$/g;

type PlaceholderValue = string | boolean | number | null | undefined;

function buildValueMap(code: ExternalLinkCode): Map<string, PlaceholderValue> {
    const values = new Map<string, PlaceholderValue>();

    for (const attribute of code.attributes) {
        // A CodeRef links to the referenced code, so its identity is the stable value to substitute.
        values.set(attribute.definitionName.toLowerCase(), attribute.referenceCode?.identity ?? attribute.value);
    }

    values.set('name', code.name);
    values.set('identity', code.identity);
    values.set('description', code.description);

    return values;
}

function hasValue(value: PlaceholderValue): value is string | boolean | number {
    return value !== null && value !== undefined && String(value).trim() !== '';
}

// Rows are maintained directly in the database, so nothing stops a template from using another
// scheme. Only http(s) may become a clickable href; javascript: and friends must stay inert.
function isHttpUrl(url: string): boolean {
    const lower = url.toLowerCase();
    return lower.startsWith('http://') || lower.startsWith('https://');
}

export function resolveExternalLink(link: LibraryExternalLink, code: ExternalLinkCode): ResolvedExternalLink {
    const values = buildValueMap(code);
    const missing: string[] = [];
    const seenMissing = new Set<string>();

    for (const match of link.urlTemplate.matchAll(PLACEHOLDER_REGEX)) {
        const placeholder = match[1];
        const key = placeholder.toLowerCase();

        if (!hasValue(values.get(key)) && !seenMissing.has(key)) {
            seenMissing.add(key);
            missing.push(placeholder);
        }
    }

    const resolved: ResolvedExternalLink = {
        text: link.displayText,
        missing
    };

    if (missing.length === 0) {
        const url = link.urlTemplate.replace(PLACEHOLDER_REGEX, (_, placeholder: string) =>
            encodeURIComponent(String(values.get(placeholder.toLowerCase())))
        );

        if (isHttpUrl(url))
            resolved.url = url;
    }

    return resolved;
}
